import * as React from "react";

import { format } from "lib/globalize-util";
import { withRouter } from "react-router";
import { IconButton, Menu, MenuItem } from "@material-ui/core";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import CreateIcon from "@material-ui/icons/Create";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import Trash from "components/icons/trash";

import me from "models/me";
import theme from "styles/theme";
import { displayingNewColorways } from "helpers/swatch-utils";
import siteConfig from "site-config";
import FloatingActionButton from "material-ui/FloatingActionButton";
import { DATATEST } from "constants/integration";
import { toDataTest } from "helpers/test";

const GALLERY = "gallery";
const LINE_SHEET = "linesheet";
const LIST = "list";

const useStyles = makeStyles({
    container: {
        position: "absolute",
        top: "4px",
        right: "4px",
        zIndex: 4,
        opacity: 0,
        transition: "all .2s ease-in-out",
        borderRadius: "50%",
        background: "rgba(255, 255, 255, 0.7)",
        height: "32px",
        width: "32px",

        "&.hover": {
            opacity: 1,
        },
        "&.list": {
            top: "8px",
            left: "none",
        },
        "& .MuiCheckbox-colorSecondary": {
            color: theme.palette.grey,
        },
        "& .Mui-checked": {
            color: theme.palette.primary1Color,
        },
    },
    contextTrigger: {
        width: 32,
        height: 32,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
});

const mStyles = {
    deleteFab: {
        position: "absolute" as "absolute",
        top: "4px",
        right: "4px",
        zIndex: 5,
    },
};

enum ProductActions {
    Edit = "Edit",
    Remove = "Remove",
    EditNotes = "EditNotes",
}
interface IContextOption {
    key: ProductActions;
    text: string;
    action: any;
    enabled: boolean;
}

interface IOwnProps {
    hover: boolean;
    isDeletable: boolean;
    bulkItems: any;
    value: any;
    onDelete: (e: any) => void;
    list: boolean;
    router: any;
    onEditProduct: () => void;
    onEditNotes: (e) => void;
    activeBrand: any;
    editable: boolean;
    catalogEntry: any;
    dnd: string;
    viewNotes: boolean;
}

const ActionMenu = ({
    hover,
    onDelete,
    router,
    onEditProduct,
    onEditNotes,
    list,
    activeBrand,
    editable,
    catalogEntry,
    dnd,
    viewNotes,
}: IOwnProps) => {
    const classes = useStyles({});

    const { pathname } = router.location;
    const allowedRoutes = [GALLERY, LINE_SHEET, LIST];
    const isAllowedRoute = new RegExp(allowedRoutes.join("|")).test(pathname);
    const removeLabel = pathname.includes(LINE_SHEET) ? "Remove from  Linesheet" : "Remove";
    const [anchorEl, setAnchorEl] = React.useState(null);
    const menu = React.useMemo(
        () => ({
            parent: {
                marginTop: "25px",
            },
            item: {
                fontFamily: "Lato",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.18px",
                color: theme.palette.darkGrey,
            },
        }),
        []
    );

    const handleOpenEditModal = React.useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            onEditProduct();
        },
        [onEditProduct]
    );

    const rawContextOptions: IContextOption[] = React.useMemo(
        () => [
            {
                key: ProductActions.Edit,
                text: format("Edit"),
                action: handleOpenEditModal,
                enabled:
                    siteConfig.EDIT_PRODUCT_ENABLED === "true" &&
                    me.hasPermission("base_edit_product") &&
                    me.hasPermission("update_product"),
            },
            {
                key: ProductActions.EditNotes,
                text: format("Edit Notes"),
                action: onEditNotes,
                enabled: editable && displayingNewColorways(activeBrand),
            },
            {
                key: ProductActions.Remove,
                text: format(removeLabel),
                action: onDelete,
                enabled: editable,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const contextOptions = React.useMemo(
        () => rawContextOptions.filter((option) => option.enabled),
        [rawContextOptions]
    );

    const handleContextTrigger = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
        },
        [setAnchorEl]
    );

    const handleContextClose = React.useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            setAnchorEl(null);
        },
        [setAnchorEl]
    );

    const handleContextAction = React.useCallback(
        (action: any) => (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
            handleContextClose(e);
            if (action) {
                action(e);
            }
        },
        [handleContextClose]
    );

    if (contextOptions.length === 0 || !isAllowedRoute) {
        return null;
    } else if (contextOptions.length === 1) {
        const { key } = contextOptions[0];

        if (key === ProductActions.Remove) {
            return catalogEntry && editable && hover && dnd !== "dragable" && !viewNotes ? (
                <FloatingActionButton
                    onClick={onDelete}
                    data-test={DATATEST.CATALOG.DELETE_ENTRY_FAB}
                    style={mStyles.deleteFab}
                    mini={true}
                    backgroundColor={theme.palette.grey}
                    zDepth={1}
                >
                    <Trash viewBox="0 0 32 32" style={{ marginLeft: 10, marginTop: 3 }} />
                </FloatingActionButton>
            ) : null;
        } else if (key === ProductActions.Edit) {
            if (list) {
                return (
                    <IconButton onClick={handleOpenEditModal} style={{ width: 48, height: 48 }}>
                        <CreateIcon style={{ color: theme.palette.darkGrey }} fontSize="large" />
                    </IconButton>
                );
            } else {
                return (
                    <div className={classnames(classes.container, { hover, list: !!list })}>
                        {hover ? (
                            <div className={classes.contextTrigger}>
                                <IconButton onClick={handleOpenEditModal}>
                                    <CreateIcon style={{ color: theme.palette.darkGrey }} />
                                </IconButton>
                            </div>
                        ) : null}
                    </div>
                );
            }
        }
    } else {
        return (
            <div className={classnames(classes.container, { hover })}>
                {hover && (
                    <div className={classes.contextTrigger}>
                        <IconButton onClick={handleContextTrigger} data-test={DATATEST.CATALOG.ENTRY_MORE_ACTIONS}>
                            <MoreHorizIcon style={{ color: theme.palette.darkGrey }} />
                        </IconButton>
                        <Menu
                            style={menu.parent}
                            anchorEl={anchorEl}
                            keepMounted
                            open={!!anchorEl}
                            onClose={handleContextClose}
                        >
                            {contextOptions.map(({ key, text, action }) => (
                                <MenuItem
                                    style={menu.item}
                                    key={key}
                                    onClick={handleContextAction(action)}
                                    data-test={`entry-action-${toDataTest(key)}`}
                                >
                                    {text}
                                </MenuItem>
                            ))}
                        </Menu>
                    </div>
                )}
            </div>
        );
    }
};

export default withRouter(ActionMenu);
